/**
 * Library feature — zod schemas for API responses and form values.
 * Two kinds (position, unit) share the same shape — one set of schemas
 * works for both.
 *
 * The backend `publicItem()` shaper returns a uniform shape for both system
 * and tenant rows. Option A (discriminated union) is preserved so downstream
 * components can narrow to `TenantItem` / `SystemItem` via `kind` guards.
 */

import { z } from 'zod';

export const LibraryKindSchema = z.enum(['position', 'unit']);
export type LibraryKind = z.infer<typeof LibraryKindSchema>;

export const TenantItemSchema = z.object({
  kind: z.literal('tenant'),
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Nullable UUID pointing back to the system row this tenant row was cloned from. */
  sourceId: z.string().nullable(),
  memberCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TenantItem = z.infer<typeof TenantItemSchema>;

export const SystemItemSchema = z.object({
  kind: z.literal('system'),
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Always null for system rows — included for type-union uniformity. */
  sourceId: z.null(),
  memberCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SystemItem = z.infer<typeof SystemItemSchema>;

export const LibraryItemSchema = z.discriminatedUnion('kind', [TenantItemSchema, SystemItemSchema]);
export type LibraryItem = z.infer<typeof LibraryItemSchema>;

export const LibraryListResponseSchema = z.object({
  items: z.array(LibraryItemSchema),
});
export type LibraryListResponse = z.infer<typeof LibraryListResponseSchema>;

const RefSchema = z.object({ id: z.string(), name: z.string() });
const JobRefSchema = z.object({ id: z.string(), title: z.string() });

/**
 * Detail responses extend the list shape with reverse-lookup arrays:
 * Staff Profiles and Job Templates whose defaults include this position/unit.
 * For tenant clones, the lookup expands to source_id so customized rows keep
 * surfacing the same relationships.
 */
export const LibraryItemDetailSchema = z.discriminatedUnion('kind', [
  TenantItemSchema.extend({
    defaultedByAgents: z.array(RefSchema),
    defaultedByJobs: z.array(JobRefSchema),
  }),
  SystemItemSchema.extend({
    defaultedByAgents: z.array(RefSchema),
    defaultedByJobs: z.array(JobRefSchema),
  }),
]);
export type LibraryItemDetail = z.infer<typeof LibraryItemDetailSchema>;

export const LibraryItemDetailResponseSchema = z.object({
  item: LibraryItemDetailSchema,
});
export type LibraryItemDetailResponse = z.infer<typeof LibraryItemDetailResponseSchema>;

/**
 * Mutation responses (createBlank, updateItem) are always tenant rows.
 */
export const LibraryItemMutationResponseSchema = z.object({
  item: TenantItemSchema,
});
export type LibraryItemMutationResponse = z.infer<typeof LibraryItemMutationResponseSchema>;

/**
 * Response from the `customize` endpoint: a new tenant row cloned from the
 * given system row, plus the count of member assignments migrated.
 */
export const LibraryCustomizeResponseSchema = z.object({
  item: TenantItemSchema,
  migratedMemberCount: z.number(),
});
export type LibraryCustomizeResponse = z.infer<typeof LibraryCustomizeResponseSchema>;

export const LibraryDeleteResponseSchema = z.object({
  deleted: z.literal(true),
  id: z.string(),
  memberCount: z.number(),
});
export type LibraryDeleteResponse = z.infer<typeof LibraryDeleteResponseSchema>;

/* Form schema for create + edit. */
export const itemFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(200),
  description: z.string().trim().max(1000).optional(),
});
export type ItemFormValues = z.infer<typeof itemFormSchema>;

/* ─── Job Templates (separate from Position/Unit Library kind) ────── */

const JobTemplateBase = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  instructions: z.string(),
  inputsDescription: z.string().nullable(),
  outputsDescription: z.string().nullable(),
  inputSchema: z.unknown().nullable(),
  outputSchema: z.unknown().nullable(),
  validateInputs: z.boolean(),
  validateOutputs: z.boolean(),
  outputValidationMode: z.enum(['strict', 'retry', 'warn']),
  sourceId: z.string().nullable(),
  defaultPositionIds: z.array(z.string()),
  defaultUnitIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const TenantJobTemplateSchema = JobTemplateBase.extend({ kind: z.literal('tenant') });
export const SystemJobTemplateSchema = JobTemplateBase.extend({ kind: z.literal('system') });
export const JobTemplateSchema = z.discriminatedUnion('kind', [
  TenantJobTemplateSchema,
  SystemJobTemplateSchema,
]);
export type TenantJobTemplate = z.infer<typeof TenantJobTemplateSchema>;
export type SystemJobTemplate = z.infer<typeof SystemJobTemplateSchema>;
export type JobTemplate = z.infer<typeof JobTemplateSchema>;

/**
 * Detail responses add the default position/unit memberships, resolved
 * server-side to tenant overrides where the caller has customized.
 */
const JobTemplateDetailExtras = {
  defaultPositions: z.array(z.object({ id: z.string(), name: z.string() })),
  defaultUnits: z.array(z.object({ id: z.string(), name: z.string() })),
  /** Tenant copy id if this tenant has already customized — null when not.
   * Surfaced on both kinds so the union stays uniform; only ever non-null
   * for system rows (a tenant row by definition isn't "customizable"). */
  customizedAsId: z.string().nullable(),
};
export const TenantJobTemplateDetailSchema =
  TenantJobTemplateSchema.extend(JobTemplateDetailExtras);
export const SystemJobTemplateDetailSchema =
  SystemJobTemplateSchema.extend(JobTemplateDetailExtras);
export const JobTemplateDetailSchema = z.discriminatedUnion('kind', [
  TenantJobTemplateDetailSchema,
  SystemJobTemplateDetailSchema,
]);
export type SystemJobTemplateDetail = z.infer<typeof SystemJobTemplateDetailSchema>;
export type JobTemplateDetail = z.infer<typeof JobTemplateDetailSchema>;

export const JobTemplateDetailResponseSchema = z.object({
  item: JobTemplateDetailSchema,
});
export type JobTemplateDetailResponse = z.infer<typeof JobTemplateDetailResponseSchema>;

export const JobTemplatesListResponseSchema = z.object({
  items: z.array(JobTemplateSchema),
});
export type JobTemplatesListResponse = z.infer<typeof JobTemplatesListResponseSchema>;

/* ─── Job Template editor — form values + response shapes ────────── */

export const jobTemplateFormSchema = z.object({
  title: z.string().trim().min(1, 'Title is required.').max(200),
  description: z.string().trim().max(2000).optional(),
  instructions: z.string().trim().min(1, 'Instructions are required.').max(20000),
  inputsDescription: z.string().trim().max(4000).optional(),
  outputsDescription: z.string().trim().max(4000).optional(),
  inputSchemaText: z.string().optional(),
  outputSchemaText: z.string().optional(),
  validateInputs: z.boolean(),
  validateOutputs: z.boolean(),
  outputValidationMode: z.enum(['strict', 'retry', 'warn']),
});
export type JobTemplateFormValues = z.infer<typeof jobTemplateFormSchema>;

export const JobTemplateMutationResponseSchema = z.object({
  item: JobTemplateSchema,
});
export type JobTemplateMutationResponse = z.infer<typeof JobTemplateMutationResponseSchema>;

export const JobTemplateCustomizeResponseSchema = z.object({
  item: JobTemplateSchema,
});
export type JobTemplateCustomizeResponse = z.infer<typeof JobTemplateCustomizeResponseSchema>;

export const JobTemplateDeleteResponseSchema = z.object({
  deleted: z.literal(true),
  id: z.string(),
});
export type JobTemplateDeleteResponse = z.infer<typeof JobTemplateDeleteResponseSchema>;

export interface JobTemplateCreateBody {
  title: string;
  description?: string | null;
  instructions: string;
  inputs_description?: string | null;
  outputs_description?: string | null;
  input_schema?: unknown;
  output_schema?: unknown;
  validate_inputs?: boolean;
  validate_outputs?: boolean;
  output_validation_mode?: 'strict' | 'retry' | 'warn';
}

export type JobTemplateUpdateBody = Partial<JobTemplateCreateBody> & {
  /** Replace the M:N Default Position assignments. Absent = no change. */
  default_position_ids?: string[];
  /** Replace the M:N Default Unit assignments. Absent = no change. */
  default_unit_ids?: string[];
};
